import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

import { useAuth } from "./AuthContext";
import { getActiveBusinessId, BUSINESS_KEY } from "./tokenStore";

/**
 * The id of the business the user currently has open, as stored when they
 * opened it from the group overview.
 *
 * Lives in localStorage rather than React state, so re-read it on every
 * navigation (opening and exiting a business both navigate) and on the
 * storage event (another tab opened a different business).
 */
export function useActiveBusinessId() {
  const location = useLocation();
  const [id, setId] = useState(getActiveBusinessId);

  useEffect(() => {
    setId(getActiveBusinessId());
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onStorage = (e) => {
      if (!e.key || e.key === BUSINESS_KEY) setId(getActiveBusinessId());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  return id;
}

/**
 * The open business as { id, name, code }, or null when none is open.
 *
 * The name is resolved from the session's memberships — /auth/refresh does
 * not return them, so the copy saved at login is what survives a refresh.
 * `name` and `code` can be null if the id is not among them; callers fall
 * back rather than showing another business's name.
 */
export function useActiveBusiness() {
  const auth = useAuth() || {};
  const { business, memberships } = auth;
  const id = useActiveBusinessId();

  return useMemo(() => {
    if (!id) return null;

    const match = (memberships || []).find(
      (m) => String(m.business_id) === String(id)
    );
    if (match) {
      return {
        id: String(id),
        name: match.business_name || null,
        code: match.business_code || null,
      };
    }

    // The token's business, but only when it is the one that is open.
    if (business && String(business.id) === String(id)) {
      return { id: String(id), name: business.name || null, code: business.code || null };
    }

    return { id: String(id), name: null, code: null };
  }, [id, business, memberships]);
}
